import type { ComponentType, ReactNode } from 'react';
import type { NombreIcono } from '@xiltrion/ui/icono';
import type { TermKey, ViewConfig } from '@planning/contracts';
import type { ProjectDetail, TaskDetail, TaskItem } from '@/api/types';

/**
 * Puntos de extensión de la web (spec §4.3). El núcleo lee de aquí; los paquetes de sector se registran desde
 * `src/extensions` al arrancar (`main.tsx`). El núcleo nunca importa un paquete.
 */
export interface RouteDef {
  path: string;
  element: ReactNode;
  module: string;
}

export interface NavDef {
  id: string;
  section: 'work' | 'management' | 'admin';
  label: string | { term: TermKey; plural?: boolean };
  icon: NombreIcono;
  href: string;
  module: string;
  permissions?: string[];
  order?: number;
}

export interface ViewProps {
  project: ProjectDetail | null;
  tasks: TaskItem[];
  view: { id: string; type: string; config: ViewConfig } | null;
  onOpenTask: (id: string) => void;
  loading: boolean;
}

export interface ViewTypeDef {
  type: string;
  label: string;
  icon: NombreIcono;
  description?: string;
  module: string;
  component: ComponentType<ViewProps>;
  /** Si la vista usa las tareas (Tabla, Kanban…) o trae sus propios datos (Archivos, Reportes). */
  needsTasks: boolean;
  scopes: Array<'project' | 'my_tasks' | 'portfolio' | 'global'>;
}

export interface SlotProps {
  project?: ProjectDetail;
  task?: TaskDetail;
  entityId?: string;
}
export type SlotName = 'project.overview' | 'project.settings' | 'task.sidebar' | 'engagement.detail' | 'corrective.plan.actions' | 'goal.detail' | 'home.widgets' | 'meeting.kinds';
export interface SlotDef {
  slot: SlotName;
  module: string;
  projectTypes?: string[];
  component: ComponentType<SlotProps>;
}

const routes: RouteDef[] = [];
const nav: NavDef[] = [];
const viewTypes = new Map<string, ViewTypeDef>();
const slots: SlotDef[] = [];

export const registry = {
  addRoute: (r: RouteDef) => void routes.push(r),
  addNav: (n: NavDef) => void nav.push(n),
  addViewType: (v: ViewTypeDef) => void viewTypes.set(v.type, v),
  addSlot: (s: SlotDef) => void slots.push(s),
  routes: () => routes,
  navItems: () => [...nav].sort((a, b) => (a.order ?? 50) - (b.order ?? 50)),
  viewType: (type: string) => viewTypes.get(type),
  viewTypes: () => [...viewTypes.values()],
  slotsFor: (slot: SlotName, has: (m: string) => boolean, projectType?: string) => slots.filter((s) => s.slot === slot && has(s.module) && (!s.projectTypes || (!!projectType && s.projectTypes.includes(projectType)))),
};
